import React, { useState, useEffect, useRef } from 'react';
import browserDetection from '@wix/santa-browser-detection';
import { runPreviewAnimation } from '../previewerUtils';

import type { PreviewerEffectsMapping } from '../types';

interface IframePreviewProps {
  html: string;
  baseWidth: number;
  displayWidth: number;
  onReady?: () => void;
  effectsMapping: PreviewerEffectsMapping;
}

function getIframeStyle(
  baseWidth: number,
  displayWidth: number,
  shouldScaleOverIframe: boolean,
): React.CSSProperties | undefined {
  if (!shouldScaleOverIframe) {
    return {};
  }
  return {
    pointerEvents: 'none',
    transformOrigin: '0 0',
    transform: `scale(${displayWidth / baseWidth})`,
  };
}

// All these functions are copied, as importing 'util' package makes a circular dependency
const getUserAgent = () => window.navigator?.userAgent;
const detected = browserDetection(getUserAgent());
const isChrome = () => !!detected.browser.chrome;
const isSafari = () => !!detected.browser.safari;

const insertPreviewHtmlToPreviewFrame = (
  previewHtml: string,
  frameDocument: Document,
  setComplete: () => void,
  frameRef: HTMLIFrameElement,
) => {
  if (!isChrome()) {
    if (!isSafari()) {
      frameRef.srcdoc = previewHtml;
      setComplete();
      return;
    }
  }

  frameDocument.open();
  frameDocument.addEventListener('readystatechange', () => {
    if (frameDocument.readyState === 'complete') {
      setComplete();
    }
  });

  frameDocument.write(previewHtml);
  frameDocument.close();
};

export const IframePreview = ({
  html,
  onReady,
  baseWidth,
  displayWidth,
  effectsMapping,
}: IframePreviewProps) => {
  const [complete, setComplete] = useState(false);

  const frameRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    if (complete && onReady) {
      onReady();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [complete]);

  useEffect(() => {
    const frameDocument: Document = frameRef.current.contentDocument;
    const onReady = () => {
      setComplete(true);
      runPreviewAnimation(frameDocument, effectsMapping);
    };
    insertPreviewHtmlToPreviewFrame(
      html,
      frameDocument,
      onReady,
      frameRef.current,
    );
  }, [effectsMapping, html]);

  const getIframeContainerStyle = () => {
    if (!frameRef.current) return {};
    return {
      height: frameRef.current.contentDocument.body.scrollHeight,
    };
  };

  const getWrapperStyle = () => {
    if (!frameRef.current) return {};
    return {
      height:
        frameRef.current.contentDocument.body.scrollHeight *
        (displayWidth / baseWidth),
      overflow: 'hidden',
    };
  };

  return (
    <div style={getWrapperStyle()}>
      <div style={getIframeContainerStyle()}>
        <iframe
          ref={frameRef}
          src="about:blank"
          width={baseWidth}
          height="100%"
          style={getIframeStyle(baseWidth, displayWidth, true)}
        />
      </div>
    </div>
  );
};
